import { useCallback, useEffect } from "react";
import { useApp } from "@/contexts/AppContext";
import { api, API_BASE } from "@/lib/api";
import { devLog } from "@/lib/log";
import { toast } from "sonner";

/**
 * Reusable "Send to accountant" primitive used by every outward-facing
 * share (Accountant Pack, BAS Worksheet, TPAR draft, Personal Tax summary).
 *
 * ▸ shareVia(kind, { text, subject, pdfUrl?, filename? })
 * ▸ prefetchPdf(pdfUrl, filename) — warm the cache. Call this from a
 *   `useEffect` in every component that has a knowable pdfUrl at mount
 *   (BAS/TPAR/Personal-Tax/Accountant-Pack panels do this). It's what
 *   makes the native share sheet appear IMMEDIATELY on tap.
 *
 * iPhone Safari share-sheet behaviour
 * ───────────────────────────────────
 *   iOS `navigator.share({files:[…]})` only works when called *inside* a
 *   user gesture with NO `await` between the click and the share call.
 *   Fetching the PDF is asynchronous, so we do it ahead of time — when
 *   the share panel renders — and stash the resulting `File` in a
 *   module-level cache keyed on the API URL. Then the tap handler is
 *   fully synchronous: cache lookup → `navigator.share({files})` → the
 *   native share sheet pops up with the PDF attached. WhatsApp / Mail /
 *   Files all appear as first-class share targets.
 *
 *   If the cache is still warming (or Web Share isn't available), we
 *   fall back to opening the PDF in a new tab via a real same-origin
 *   `<a href="/api/…/pdf" target="_blank">` click. Safari's built-in PDF
 *   viewer surfaces its own Share icon top-right so the tradie still has
 *   a clean path to WhatsApp/Mail — they just tap once more instead of
 *   zero. No pop-up-blocker prompt, no `blob:` URL, no "user is stuck".
 */

// Module-level cache: pdfUrl → File. Shared across every hook instance
// so a BAS page that pre-fetches the pack PDF benefits the Ledger row
// that shares the same URL. Bounded via a manual size cap (10 entries)
// to keep memory sane on long sessions.
const PDF_CACHE = new Map();
const PDF_CACHE_MAX = 10;
const PDF_INFLIGHT = new Map(); // pdfUrl → Promise<File|null>

function cachePdfFile(pdfUrl, file) {
  if (!pdfUrl || !file) return;
  if (PDF_CACHE.size >= PDF_CACHE_MAX) {
    // Drop oldest entry — Map preserves insertion order.
    const oldestKey = PDF_CACHE.keys().next().value;
    PDF_CACHE.delete(oldestKey);
  }
  PDF_CACHE.set(pdfUrl, file);
  // Debug: expose to window for QA smoke tests.
  if (typeof window !== "undefined") {
    window.__bi_pdf_cache_keys = Array.from(PDF_CACHE.keys());
  }
}

async function fetchPdfFile(pdfUrl, filename) {
  if (PDF_CACHE.has(pdfUrl)) return PDF_CACHE.get(pdfUrl);
  if (PDF_INFLIGHT.has(pdfUrl)) return PDF_INFLIGHT.get(pdfUrl);
  const p = (async () => {
    try {
      const { data: blob } = await api.get(pdfUrl, { responseType: "blob" });
      const file = new File([blob], filename, { type: "application/pdf" });
      cachePdfFile(pdfUrl, file);
      return file;
    } catch (err) {
      devLog.warn("useShareToAccountant: pdf prefetch failed", err);
      return null;
    } finally {
      PDF_INFLIGHT.delete(pdfUrl);
    }
  })();
  PDF_INFLIGHT.set(pdfUrl, p);
  return p;
}

export function useShareToAccountant() {
  const { profile } = useApp();
  const bizName = profile?.business_name || "";
  const abn = profile?.abn || "";
  const accountantName = profile?.accountant_name || "";
  const accountantEmail = profile?.accountant_email || "";

  const canShareFiles = (file) =>
    typeof navigator !== "undefined" &&
    typeof navigator.canShare === "function" &&
    navigator.canShare({ files: [file] });

  const openPdfInNewTab = useCallback(async (pdfUrl, filename) => {
    // PWA-safe hand-off — NEVER `window.location = url` or
    // `<a target="_blank">` for PDFs. On installed iOS PWAs those routes
    // strand the tradie inside Safari's PDF viewer with no way back to
    // the app. Instead: fetch the file as a blob, try Web Share, then
    // fall back to a same-tab `<a download>` on a blob URL.
    try {
      const cached = await fetchPdfFile(pdfUrl, filename);
      if (cached && canShareFiles(cached)) {
        try {
          await navigator.share({ files: [cached], title: filename });
          return;
        } catch (err) {
          if (err && err.name === "AbortError") return;  // Cancel returns to sheet
          // fall through to blob-URL save
        }
      }
      const blob = cached
        ? cached
        : await api.get(pdfUrl, { responseType: "blob" }).then((r) => r.data);
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename || "buildintel.pdf";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
    } catch (err) {
      devLog.warn("openPdfInNewTab: blob fallback failed", err);
      toast.error("Couldn't open the PDF — try again in a moment");
    }
  }, []);

  /**
   * Kick off a background fetch of the PDF so the cache is warm when the
   * tradie taps Share. Idempotent + de-duplicated (in-flight promises
   * are shared). Safe to call from a `useEffect` on every render.
   */
  const prefetchPdf = useCallback((pdfUrl, filename) => {
    if (!pdfUrl) return;
    fetchPdfFile(pdfUrl, filename || "buildintel.pdf");
  }, []);

  /**
   * Fire `navigator.share({files})` SYNCHRONOUSLY from the click handler
   * (no awaits before the call) so iOS Safari honours the user gesture.
   * Returns true if we successfully invoked the share dialog.
   */
  const trySyncShare = useCallback((pdfFile, { text, subject, channel }) => {
    if (
      !pdfFile ||
      typeof navigator === "undefined" ||
      typeof navigator.share !== "function" ||
      !canShareFiles(pdfFile)
    ) {
      return null;
    }
    const safeSubject = subject || (text ? text.slice(0, 60) : "BuildIntel AI");
    try {
      // Kick off — do NOT await here. The `.then/.catch` handlers run
      // after the gesture completes, which is fine because
      // `navigator.share` has already been *invoked* inside the gesture
      // and iOS keeps the sheet open until the user dismisses it.
      const promise = navigator.share({
        title: safeSubject,
        text: text || "",
        files: [pdfFile],
      });
      promise
        .then(() => toast.success("Shared with PDF attached"))
        .catch((err) => {
          if (err?.name === "AbortError") return;
          devLog.warn(`share(${channel}) rejected → fallback to new tab`, err);
        });
      return promise;
    } catch (err) {
      devLog.warn(`share(${channel}) threw sync → fallback to new tab`, err);
      return null;
    }
  }, []);

  const shareVia = useCallback(
    async (kind, { text, subject, pdfUrl, filename = "buildintel.pdf" } = {}) => {
      if (!text) return;
      const safeSubject = subject || text.slice(0, 60);

      // ── Text-only branches (no PDF) — keep the existing simple UX. ──
      if (!pdfUrl) {
        if (kind === "email") {
          const to = encodeURIComponent(accountantEmail);
          // Use window.open with _blank so the installed PWA process is
          // not replaced by the mailto: hand-off on iOS. Falls back to
          // same-tab `mailto:` only if the browser blocks the popup.
          const mailto = `mailto:${to}?subject=${encodeURIComponent(safeSubject)}&body=${encodeURIComponent(text)}`;
          const opened = window.open(mailto, "_blank", "noopener,noreferrer");
          if (!opened) window.location.href = mailto;
          return;
        }
        if (kind === "native" && typeof navigator?.share === "function") {
          try {
            await navigator.share({ title: safeSubject, text });
            toast.success("Summary shared");
            return;
          } catch (err) {
            if (err?.name === "AbortError") return;
            devLog.warn("native share (text-only) failed → wa.me", err);
          }
        }
        window.open(
          `https://wa.me/?text=${encodeURIComponent(text)}`,
          "_blank",
          "noopener,noreferrer"
        );
        return;
      }

      // ── PDF branch — try SYNC Web Share via the pre-warmed cache ────
      // Cache lookup is synchronous, so if `prefetchPdf` was called on
      // component mount the file is ready by the time the tradie taps
      // Share (usually the PDF is <500 KB and pre-fetches in <1 s).
      const cachedFile = PDF_CACHE.get(pdfUrl);
      if (typeof window !== "undefined") {
        window.__bi_share_debug = {
          kind,
          pdfUrl,
          cachedHit: !!cachedFile,
          cacheSize: PDF_CACHE.size,
          cacheKeys: Array.from(PDF_CACHE.keys()),
        };
      }
      if (cachedFile) {
        const p = trySyncShare(cachedFile, {
          text,
          subject: safeSubject,
          channel: kind,
        });
        if (p) {
          // Fire-and-forget — the OS share sheet is now up. We're done.
          return;
        }
      }

      // ── Fallback — always open in a new tab so the tradie is never
      //    stuck. Safari's PDF viewer surfaces its own Share icon; on
      //    Android Chrome the PDF opens in the Chrome PDF viewer which
      //    also has a Share option.
      openPdfInNewTab(pdfUrl, filename);
      const label =
        cachedFile == null
          ? "Preparing PDF — tap the Share icon in the new tab"
          : "Opening PDF — tap the Share icon in the new tab";
      toast.success(label);

      // Prime the cache in the background so a SECOND tap goes through
      // the fast sync-share path.
      fetchPdfFile(pdfUrl, filename);
    },
    [accountantEmail, openPdfInNewTab, trySyncShare]
  );

  return {
    bizName,
    abn,
    accountantName,
    accountantEmail,
    shareVia,
    prefetchPdf,
  };
}

/**
 * Convenience hook — pre-fetches the PDF as soon as the component mounts
 * (or when `pdfUrl` changes). Callers pass the same URL they will pass
 * to `shareVia({pdfUrl})` so the cache hits at tap time.
 */
export function useShareToAccountantWithPdf(pdfUrl, filename) {
  const share = useShareToAccountant();
  useEffect(() => {
    if (pdfUrl) share.prefetchPdf(pdfUrl, filename);
  }, [pdfUrl, filename, share]);
  return share;
}
